import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { ChatCircleDots, X, PaperPlaneRight, Robot } from "@phosphor-icons/react";

function getSessionId() {
  let id = localStorage.getItem("ac_chat_session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("ac_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: "Hey there! ⚡ I'm Connie. Ask me anything about posting links, pricing, reviews or getting connected!" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const uid = `u-${Date.now()}`;
    const aid = `a-${Date.now()}`;
    setMessages((m) => [...m, { id: uid, role: "user", content: text }, { id: aid, role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Sorry, I couldn't connect. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={TID.chatToggle}
        className="fixed bottom-24 right-6 z-50 w-16 h-16 rounded-full bg-[#FF007F] flex items-center justify-center text-white shadow-[0_0_30px_rgba(255,0,127,0.7)] hover:scale-110 transition-transform"
        aria-label="Open chat"
      >
        {open ? <X size={28} weight="bold" /> : <ChatCircleDots size={30} weight="fill" />}
      </button>

      {open && (
        <div className="fixed bottom-44 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col bg-[#0f0f12] border-2 border-[#FF007F] shadow-[0_0_40px_rgba(255,0,127,0.4)]">
          <div className="flex items-center gap-3 p-4 border-b border-zinc-800 bg-zinc-950">
            <div className="w-9 h-9 rounded-full bg-[#00FFFF] flex items-center justify-center">
              <Robot size={20} weight="bold" color="#000" />
            </div>
            <div>
              <p className="font-display font-bold text-sm">Connie</p>
              <p className="text-[10px] uppercase tracking-widest text-[#39FF14] font-mono">● Online</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#FF007F] text-white" : "bg-zinc-800 text-zinc-100"
                }`} data-testid={`chat-msg-${m.role}`}>
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-zinc-800 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask Connie…"
              data-testid={TID.chatInput}
              className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-2 text-sm text-white"
            />
            <button onClick={send} disabled={busy} data-testid={TID.chatSend}
              className="px-3 bg-[#00FFFF] text-black hover:bg-[#FF007F] hover:text-white transition-colors disabled:opacity-50">
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
